import { Calendar as CalendarIcon, ChevronDown, ChevronLeft, ChevronRight, X } from "lucide-react";
import { useEffect, useId, useRef, useState } from "react";

import { useT } from "@/i18n/provider";
import { addDays, daysInMonth, formatDate, mondayIndex, parseIso, today as pharmacyToday, toIso } from "@/utils/dates";
import { Button } from "./button";
import { cx } from "./cx";
import { Dialog } from "./dialog";

/**
 * A date, picked from a calendar, never typed (a copy of
 * apps/mobile/src/components/ui/date-field.tsx).
 *
 * "01/03/2027" is the first of March in a Somali pharmacy and the third of
 * January on a computer set up in America; a calendar cannot be read two
 * ways. Shown as DD/MM/YYYY; the value is YYYY-MM-DD, what the server wants.
 *
 * The calendar: arrows change the month; the heading zooms out (days →
 * months → years), so an expiry three years away is three clicks. With the
 * keyboard, the arrow keys move a day or a week, Page Up/Down a month, Enter
 * picks, Escape closes.
 */

interface CalendarChoice {
  /** Where it opens when nothing is chosen yet: "year" for an expiry date. */
  startWith?: "day" | "year";
  /** Days after this cannot be picked (stock cannot arrive tomorrow). YYYY-MM-DD. */
  maxDate?: string;
  /** A Today button, for dates that are usually today. */
  showToday?: boolean;
}

export function DateField({
  label,
  value,
  onChange,
  hint,
  error,
  startWith = "day",
  maxDate,
  showToday,
}: CalendarChoice & {
  label: string;
  /** YYYY-MM-DD, or null for nothing chosen yet. */
  value: string | null;
  onChange: (value: string) => void;
  hint?: string;
  error?: string;
}) {
  const t = useT();
  const [open, setOpen] = useState(false);
  const labelId = useId();
  const noteId = useId();
  const note = error ?? hint;

  return (
    <div className="flex flex-col gap-1">
      <span id={labelId} className="text-label font-semibold text-text">
        {label}
      </span>
      <button
        type="button"
        onClick={() => setOpen(true)}
        aria-labelledby={labelId}
        aria-describedby={note ? noteId : undefined}
        aria-haspopup="dialog"
        aria-invalid={error ? true : undefined}
        className={cx(
          "flex min-h-12 items-center gap-2 rounded-control border-[1.5px] bg-surface px-3 text-left transition-colors",
          "hover:bg-primary-soft",
          error ? "border-danger" : "border-input-border",
        )}
      >
        <span className={cx("flex-1 text-input", value ? "text-text" : "text-muted")}>
          {value ? formatDate(value) : t.webChooseDate}
        </span>
        <CalendarIcon size={22} aria-hidden="true" className="text-primary" />
      </button>
      {error ? (
        <p id={noteId} role="alert" className="text-small font-medium text-danger">
          {error}
        </p>
      ) : hint ? (
        <p id={noteId} className="text-small text-muted">
          {hint}
        </p>
      ) : null}

      {open && (
        <CalendarDialog
          title={label}
          value={value}
          startWith={startWith}
          maxDate={maxDate}
          showToday={showToday}
          onPick={(picked) => {
            setOpen(false);
            onChange(picked);
          }}
          onClose={() => setOpen(false)}
        />
      )}
    </div>
  );
}

/**
 * A date as a small chip, for narrowing a list to one day (Sales history).
 * Empty, it says what it is for; picked, it shows the day and an × that goes
 * back to every day.
 */
export function DateChip({
  label,
  value,
  onChange,
  maxDate,
  showToday,
}: Omit<CalendarChoice, "startWith"> & {
  label: string;
  value: string | null;
  onChange: (value: string | null) => void;
}) {
  const t = useT();
  const [open, setOpen] = useState(false);
  const on = value !== null;

  return (
    <span
      className={cx(
        "inline-flex min-h-10 py-1.5 text-center leading-snug shrink-0 items-center rounded-full border transition-colors",
        on ? "border-primary bg-primary-soft text-primary" : "border-border bg-surface text-body hover:border-primary",
      )}
    >
      <button
        type="button"
        onClick={() => setOpen(true)}
        aria-haspopup="dialog"
        aria-label={on ? `${label}: ${formatDate(value)}` : label}
        className={cx("inline-flex h-full items-center gap-1 text-label font-semibold", on ? "pr-1 pl-4" : "px-4")}
      >
        <CalendarIcon size={16} aria-hidden="true" />
        <span>{on ? formatDate(value) : label}</span>
      </button>
      {on && (
        <button
          type="button"
          onClick={() => onChange(null)}
          aria-label={t.allDates}
          className="inline-flex h-full items-center pr-3 pl-1"
        >
          <X size={16} aria-hidden="true" />
        </button>
      )}
      {open && (
        <CalendarDialog
          title={label}
          value={value}
          startWith="day"
          maxDate={maxDate}
          showToday={showToday}
          onPick={(picked) => {
            setOpen(false);
            onChange(picked);
          }}
          onClose={() => setOpen(false)}
        />
      )}
    </span>
  );
}

type View = "day" | "month" | "year";

/** One page of the year view: a 3-by-4 grid. */
const YEARS_PER_PAGE = 12;

/** The same day `months` later, kept inside the month (31 Jan + 1 → 28/29 Feb). */
function addMonths(iso: string, months: number): string {
  const p = parseIso(iso)!;
  const m = p.month + months;
  const year = p.year + Math.floor(m / 12);
  const month = ((m % 12) + 12) % 12;
  return toIso(year, month, Math.min(p.day, daysInMonth(year, month)));
}

export function CalendarDialog({
  title,
  value,
  startWith = "day",
  maxDate,
  showToday,
  onPick,
  onClose,
}: CalendarChoice & {
  title: string;
  value: string | null;
  onPick: (value: string) => void;
  onClose: () => void;
}) {
  const t = useT();
  const today = pharmacyToday();
  const todayParts = parseIso(today)!;
  const chosen = parseIso(value);

  const [cursor, setCursor] = useState(() => ({
    year: chosen?.year ?? todayParts.year,
    month: chosen?.month ?? todayParts.month,
  }));
  const [view, setView] = useState<View>(chosen ? "day" : startWith);
  /** First year on the year page: a little behind, mostly ahead. */
  const [yearPage, setYearPage] = useState(() => (chosen?.year ?? todayParts.year) - 3);
  /** The day the keyboard is on. */
  const [focusDay, setFocusDay] = useState(() => value ?? (maxDate && today > maxDate ? maxDate : today));
  const moved = useRef(false);
  const grid = useRef<HTMLDivElement>(null);

  const allowed = (iso: string) => !maxDate || iso <= maxDate;

  // Zooming in or out redraws the grid: put focus on its chosen cell, so the
  // keyboard does not fall back to the page behind.
  const firstView = useRef(true);
  useEffect(() => {
    if (firstView.current) {
      firstView.current = false;
      return;
    }
    grid.current?.querySelector<HTMLButtonElement>("[data-autofocus]")?.focus();
  }, [view]);

  // After the keyboard moves, focus follows (and the page turns if it must).
  useEffect(() => {
    if (!moved.current) return;
    grid.current?.querySelector<HTMLButtonElement>(`[data-day="${focusDay}"]`)?.focus();
  }, [focusDay, cursor]);

  function moveFocus(next: string) {
    const p = parseIso(next)!;
    moved.current = true;
    setFocusDay(next);
    if (p.year !== cursor.year || p.month !== cursor.month) setCursor({ year: p.year, month: p.month });
  }

  function onDayKey(e: React.KeyboardEvent) {
    const steps: Record<string, () => string> = {
      ArrowLeft: () => addDays(focusDay, -1),
      ArrowRight: () => addDays(focusDay, 1),
      ArrowUp: () => addDays(focusDay, -7),
      ArrowDown: () => addDays(focusDay, 7),
      PageUp: () => addMonths(focusDay, -1),
      PageDown: () => addMonths(focusDay, 1),
    };
    const step = steps[e.key];
    if (!step) return;
    e.preventDefault();
    moveFocus(step());
  }

  const heading =
    view === "day"
      ? `${t.monthNames[cursor.month]} ${cursor.year}`
      : view === "month"
        ? String(cursor.year)
        : `${yearPage} – ${yearPage + YEARS_PER_PAGE - 1}`;

  function step(direction: -1 | 1) {
    if (view === "day") {
      const m = cursor.month + direction;
      setCursor({ year: cursor.year + Math.floor(m / 12), month: ((m % 12) + 12) % 12 });
    } else if (view === "month") {
      setCursor({ ...cursor, year: cursor.year + direction });
    } else {
      setYearPage(yearPage + direction * YEARS_PER_PAGE);
    }
  }

  function zoomOut() {
    if (view === "day") setView("month");
    else if (view === "month") {
      setYearPage(cursor.year - 3);
      setView("year");
    }
  }

  const cellBase = "flex items-center justify-center rounded-full text-base transition-colors";

  function dayGrid() {
    const blanks = mondayIndex(cursor.year, cursor.month);
    const count = daysInMonth(cursor.year, cursor.month);
    const cells: Array<number | null> = [
      ...Array.from({ length: blanks }, () => null),
      ...Array.from({ length: count }, (_, i) => i + 1),
    ];
    while (cells.length % 7 !== 0) cells.push(null);
    // The keyboard's day, if it is on this page; else the 1st, so Tab can get in.
    const inPage = parseIso(focusDay);
    const tabDay =
      inPage && inPage.year === cursor.year && inPage.month === cursor.month
        ? focusDay
        : toIso(cursor.year, cursor.month, 1);

    return (
      <div ref={grid} role="grid" aria-label={heading} onKeyDown={onDayKey} className="flex flex-col gap-0.5">
        <div role="row" className="grid grid-cols-7">
          {t.weekdaysShort.map((d) => (
            <span key={d} role="columnheader" className="py-1 text-center text-small font-semibold text-muted">
              {d}
            </span>
          ))}
        </div>
        {Array.from({ length: cells.length / 7 }, (_, r) => (
          <div key={r} role="row" className="grid grid-cols-7">
            {cells.slice(r * 7, r * 7 + 7).map((day, c) => {
              if (day === null) return <span key={c} role="gridcell" />;
              const iso = toIso(cursor.year, cursor.month, day);
              const isChosen = iso === value;
              const enabled = allowed(iso);
              return (
                <span key={c} role="gridcell" className="flex justify-center">
                  <button
                    type="button"
                    data-day={iso}
                    tabIndex={iso === tabDay ? 0 : -1}
                    data-autofocus={iso === tabDay ? "" : undefined}
                    disabled={!enabled}
                    aria-pressed={isChosen}
                    aria-label={formatDate(iso)}
                    aria-current={iso === today ? "date" : undefined}
                    onClick={() => onPick(iso)}
                    onFocus={() => setFocusDay(iso)}
                    className={cx(
                      cellBase,
                      "size-11",
                      iso === today && "border-[1.5px] border-primary",
                      isChosen ? "bg-primary font-bold text-on-primary" : "text-text hover:bg-primary-soft",
                      !enabled && "cursor-not-allowed text-disabled hover:bg-transparent",
                    )}
                  >
                    {day}
                  </button>
                </span>
              );
            })}
          </div>
        ))}
      </div>
    );
  }

  function bigGrid(
    items: Array<{
      key: string;
      label: string;
      chosen: boolean;
      current?: boolean;
      enabled: boolean;
      pick: () => void;
    }>,
  ) {
    // Where focus starts: the chosen one, else the current one, else the first that can be picked.
    const start =
      items.find((it) => it.chosen) ?? items.find((it) => it.current && it.enabled) ?? items.find((it) => it.enabled);
    return (
      <div ref={grid} className="grid grid-cols-3 gap-2">
        {items.map((it) => (
          <button
            key={it.key}
            type="button"
            data-autofocus={it === start ? "" : undefined}
            disabled={!it.enabled}
            aria-pressed={it.chosen}
            onClick={it.pick}
            className={cx(
              "min-h-12 rounded-control border font-semibold transition-colors",
              it.current && "border-[1.5px] border-primary",
              it.chosen ? "border-primary bg-primary text-on-primary" : "border-border text-text hover:bg-primary-soft",
              !it.enabled && "cursor-not-allowed text-disabled hover:bg-transparent",
            )}
          >
            {it.label}
          </button>
        ))}
      </div>
    );
  }

  const monthGrid = () =>
    bigGrid(
      t.monthNames.map((name, month) => ({
        key: name,
        label: name,
        chosen: chosen?.year === cursor.year && chosen.month === month,
        // A month is off only if it lies wholly after the last allowed day.
        enabled: allowed(toIso(cursor.year, month, 1)),
        pick: () => {
          setCursor({ year: cursor.year, month });
          setView("day");
        },
      })),
    );

  const yearGrid = () =>
    bigGrid(
      Array.from({ length: YEARS_PER_PAGE }, (_, i) => yearPage + i).map((year) => ({
        key: String(year),
        label: String(year),
        chosen: chosen?.year === year,
        current: year === todayParts.year,
        enabled: allowed(toIso(year, 0, 1)),
        pick: () => {
          setCursor({ year, month: cursor.month });
          setView("month");
        },
      })),
    );

  return (
    <Dialog
      title={title}
      onClose={onClose}
      footer={
        <>
          <Button variant="quiet" label={t.cancel} onClick={onClose} />
          {showToday && allowed(today) && <Button variant="secondary" label={t.today} onClick={() => onPick(today)} />}
        </>
      }
    >
      <div className="flex items-center justify-between">
        <button
          type="button"
          onClick={() => step(-1)}
          aria-label={t.previous}
          className="flex size-10 items-center justify-center rounded-full text-primary hover:bg-primary-soft"
        >
          <ChevronLeft size={24} aria-hidden="true" />
        </button>
        <button
          type="button"
          onClick={zoomOut}
          disabled={view === "year"}
          aria-live="polite"
          className="flex min-h-10 items-center gap-1 rounded-control px-3 text-heading font-bold text-text hover:bg-primary-soft disabled:hover:bg-transparent"
        >
          {heading}
          {view !== "year" && <ChevronDown size={18} aria-hidden="true" className="text-primary" />}
        </button>
        <button
          type="button"
          onClick={() => step(1)}
          aria-label={t.next}
          className="flex size-10 items-center justify-center rounded-full text-primary hover:bg-primary-soft"
        >
          <ChevronRight size={24} aria-hidden="true" />
        </button>
      </div>
      {view === "day" ? dayGrid() : view === "month" ? monthGrid() : yearGrid()}
    </Dialog>
  );
}
